(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD
    define(['jquery', 'underscore', 'torso/modules/ServiceCell', 'templates'], factory);
  } else if (typeof exports === 'object') {
    // Node, CommonJS-like
    var $ = require('jquery');
    var _ = require('underscore');
    var TorsoService = require('backbone-torso/modules/ServiceCell');
    var templates = {
      sharedContainerTemplate: require('./sharedContainerTemplate.hbs'),
      translucentBackgroundTemplate: require('./translucentBackgroundTemplate.hbs')
    };
    module.exports = factory($, _, TorsoService, templates);
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function($, _, TorsoService, templates) {
  'use strict';

  /**
   * Singleton service for modals.
   *
   * This keeps a stack of modals, in the dom at the bottom of the page (outside of any perspective/view's rendering.
   * It is accessed exclusively by UseModalBehavior.js and ModalBehavior.js. Modals are still treated as children
   * (obeying lifecycle), despite being in 'outside' DOM.
   *
   * If a modal (or something up its parental chain) is detached, then the modal will be 'hidden' until a re-attach occurs.
   *
   * Only the top-most active (non-hidden) modal is supposed to receive interaction. Modals use a translucent blocking div
   * to prevent the mouse from interacting from things 'below.' The Service also intercepts 'focusin' events to prevent
   * elements from outside the modal from receiving keyboard focus (via tab, shift-tab, etc.).
   *
   * Each modal attempts to remember the dom element that was keyboard-active when it was summoned, so that element (if it
   * still exists) can again receive focus when the modal is dismissed (or hidden).
   *
   * @class modalService
   * @static
   * @extends Torso.ServiceCell
   */
  return new (TorsoService.extend({

    /**
     * @method initialize
     */
    initialize: function() {
      this._modalStack = [];
      this._topActiveModal = null;

      // listen to all focusin events, so we can disable focus on things not in the top-most active modal
      $('body').on('focusin', _.bind(this._handleFocusin, this));
    },

    /**
     * Puts the modalView on the stack, starts listening to it for modal events,
     * and displays it as a modal.
     *
     * Throws an error if you try to show the same modal instance twice (one shouldn't exist
     * in two places in the stack). Two instances of the same view can be shown in the stack,
     * though.
     *
     * Throws an error if either the parent or modal view doesn't use the appropriate behavior.
     *
     * @method showModal
     * @param parentView {View} the view instance that is the parent of the modal.
     * @param modalView {View} the view instance to display as a modal.
     * @param [resolveCallback=undefined] {function(results)} if present, will be called when the modal is resolved.
     * @param [rejectCallback=undefined] {function(results)} if present, will be called when the modal is rejected.
     */
    showModal: function(parentView, modalView, resolveCallback, rejectCallback) {
      var modalStackObject = this._getModalObject(modalView);
      if (modalStackObject) {
        throw new Error('can\'t use the same modal instance more than once at a time!');
      }
      if (_.indexOf(_.allKeys(parentView), 'showModal') < 0) {
        throw new Error('Parent must use UseModalBehavior!');
      }
      if (_.indexOf(_.allKeys(modalView), 'resolveModal') < 0) {
        throw new Error('Modal must use ModalBehavior!');
      }

      // default callbacks do nothing
      resolveCallback = resolveCallback || _.noop;
      rejectCallback = rejectCallback || _.noop;

      // store active element so we can resume it when modal closes
      var $savedActiveElement = $(document.activeElement);

      // Creates the divs that make the modal a modal, and attaches the modal view to the newly-created
      // (nested) injection site.
      if ($('body').children('[data-shared-container]').length < 1) {
        $('body').append(templates.sharedContainerTemplate());
      }
      var $modalElAnchor = $('body').children('[data-shared-container]');
      var $placeholderEL = $(templates.translucentBackgroundTemplate());
      var $modalEl = $placeholderEL.children('[data-translucent-background]');
      $modalElAnchor.append($placeholderEL);
      var attachmentEl = $modalEl.find('[inject="modal-content"]');
      // make child of parent view, attached to service-controlled DOM
      parentView.attachView(attachmentEl, modalView);

      // create object and put on stack
      modalStackObject = {
        parentView: parentView,
        modalView: modalView,
        $savedActiveElement: $savedActiveElement,
        $placeholderEL: $placeholderEL,
        $modalEl: $modalEl,
        callbacks: {
          resolve: resolveCallback,
          reject: rejectCallback
        },
        hidden: false
      };
      this._modalStack.push(modalStackObject);
      this._topActiveModal = modalStackObject;

      // remove focus from whatever had it before
      $savedActiveElement.blur();

      // listen to modal's events
      this.listenTo(modalView, 'modal:hideModal', this._hideModal);
      this.listenTo(modalView, 'modal:cleanUpModal', this._cleanUpModal);
      this.listenTo(modalView, 'modal:resolveModal', this._resolveModal);
      this.listenTo(modalView, 'modal:rejectModal', this._rejectModal);

      this._handleBodyScroll();

      this._scrollModalIntoView();
    },

    /**
     * Check to see if the modal instance for the given modalView is already in the stack.
     * Returns true even if the modal is 'hidden'.
     *
     * @method isModalInStack
     * @param modalView {View} the view instance to check
     * @returns {boolean} whether the modal is already in the stack
     */
    isModalInStack: function(modalView) {
      return !!this._getModalObject(modalView);
    },

    /**
     * Check to see if the modal instance for the given modalView is the top-most active modal in the stack.
     *
     * @method isTopActiveModal
     * @param modalView {View} the view instance to check
     * @returns {boolean} whether the modal is the top-most active one
     */
    isTopActiveModal: function(modalView) {
      var modalStackObject = this._getModalObject(modalView);
      return modalStackObject === this._topActiveModal;
    },

    /**
     * Finds the given modalView in the _modalStack, and returns the stacked object.
     *
     * @method _getModalObject
     * @param modalView {View} the view instance to look up.
     * @return {Object} contains the view, the dom element, and the pair of callbacks.
     * @private
     */
    _getModalObject: function(modalView) {
      return _.find(this._modalStack, function(modalStackObject) {
        return modalStackObject.modalView === modalView;
      }, this);
    },

    /**
     * Redefines this._topActiveModal (intended to be called after hide/reshow).
     *
     * @method _recalculateTopActiveModal
     * @private
     */
    _recalculateTopActiveModal: function() {
      var topActiveModal = null;
      for (var i = this._modalStack.length - 1; i >= 0; i--) {
        var currentObject = this._modalStack[i];
        if (!currentObject.hidden) {
          topActiveModal = currentObject;
          break;
        }
      }
      this._topActiveModal = topActiveModal;
    },

    /**
     * Gets the set of tabbable (by html specs) children of the given parent.
     *
     * @method _getTabbableChildren
     * @param parent {Object|string} the parent jquery object (or selector for such) to search under.
     * @param doSort {boolean} whether or not to sort the selected objects by real tabbing order (accounting for tabindex > 0)
     * @private
     */
    _getTabbableChildren: function(parent, doSort) {
      // this is meant to substitute for jquery-ui's :tabbable selector
      var tabbableSelector = 'a[href]:not([disabled]):visible,'
                             + ' button:not([disabled]):visible,'
                             + ' input:not([disabled]):visible,'
                             + ' select:not([disabled]):visible,'
                             + ' textarea:not([disabled]):visible,'
                             + ' object:not([disabled]):visible,'
                             + ' [name].area[href]:not([disabled]):visible,'
                             + ' [tabindex]:not([disabled]):not([tabindex="-1"]):visible';
      var tabbables = $(parent).find(tabbableSelector).toArray();
      if (doSort) {
        _.sortBy(tabbables, function(el) {
          var tabindex = $.attr(el, 'tabindex');
          if (isNaN(tabindex) || tabindex < 1) {
            // tabindex of 0, or no tabindex, should be at end (in DOM order)
            return Number.MAX_SAFE_INTEGER;
          } else {
            return tabindex;
          }
        });
      }
      return tabbables;
    },

    /**
     * Disable scroll on body when any modal is shown.
     *
     * @method handleBodyScroll
     * @private
     */
    _handleBodyScroll: function() {
      var noModalShown = document.querySelectorAll('[data-translucent-background]').length < 1;

      var onlyContainsHiddenAdminAboutModal = this._checkAdminToolsAboutModal();

      if (noModalShown || onlyContainsHiddenAdminAboutModal) {
        $('body').removeClass('brec-dialog__background');
      } else {
        $('body').addClass('brec-dialog__background');
      }
    },

    /**
     * Check if there's only a hidden admin tools about modal in the DOM.
     *
     * Admin tools about modal is written in jsp and uses "display: none" to show/hide modal
     * rather than injection. Remove this function when the jsp nav bar in admin tools is
     * replaced with Torso nav bar.
     *
     * @method _checkAdminToolsAboutModal
     * @private
     * @returns {Boolean} true if the DOM contains only a hidden admin tools about modal
     */
    _checkAdminToolsAboutModal: function() {
      var currentModals = document.querySelectorAll('[data-translucent-background]');
      var adminToolsAboutModal = document.querySelector('[data-about-modal]');
      var onlyContainsHiddenAdminAboutModal;
      if (currentModals.length === 1 && adminToolsAboutModal) {
        onlyContainsHiddenAdminAboutModal = adminToolsAboutModal.style.display === 'none';
      }
      return onlyContainsHiddenAdminAboutModal;
    },

    /**
     * Scroll the modal into the middle of the screen.
     *
     * The modal can be scrolled in admin tool when displayed in an iframe inside the parent page.
     *
     * Scroll modal into view by first detecting if it is in an iFrame in admin tool.
     * This magically puts the modal in the middle of the page w/o much fuss.  jQuery animations don't seem to work
     * from inside the iframe to scroll the entire window, but window.scroll() does from the parent window.
     *
     * @method _scrollModalIntoView
     * @private
     */
    _scrollModalIntoView: function() {
      // If you are in an iframe, then the top window is not the iframe's window.
      var isInIFrame = window.top !== window;
      // Get a jQ
      var $iFrameParentDocument = $(window.top.document);
      // Very brittle attempt to be sure that we are iFramed in on the admin patient or visit details.
      var $thisIFrame = $iFrameParentDocument.find('iframe:visible.tab__content');

      // Good enough for admin iFrame - there is only one visible iFrame at any time.
      if (isInIFrame && $thisIFrame.length === 1) {
        var iFrameInParentOffset = $thisIFrame.offset();
        // Null-safe get of the top of the iframe - will default to 0 if no iframe is found.
        var iFrameTopFromWindowTop = (iFrameInParentOffset && iFrameInParentOffset.top) || 0;
        var $modalViewEl = this._topActiveModal && this._topActiveModal.modalView.$el;
        var modalViewOffsetFromIFrame = $modalViewEl && $modalViewEl.offset();
        var modalViewHeight = ($modalViewEl && $modalViewEl.height()) || 0;
        var modalViewMiddleFromTopOfModal = modalViewHeight / 2;
        var topOfModalFromIframeTop = (modalViewOffsetFromIFrame && modalViewOffsetFromIFrame.top) || 0;

        // Get the height of the fixed elements in the admin tool layout.  Fixed elements are the ones that do not
        //   move when you scroll, so we need to remove their combined height from the browser view port to get the actual
        //   viewable area on the page.
        // <nav> is very specific to admin-tool layout.
        var fixedNavHeight = $iFrameParentDocument.find('nav').height() || 0;
        // page_details is very specific to admin-tool layout.
        var fixedPageDetailsHeight = $iFrameParentDocument.find('.page_details').height() || 0;
        var fixedElementsHeight = fixedNavHeight + fixedPageDetailsHeight;

        // Now calculate where the center of the viewport is with the fixed elements removed.
        var viewPortHeight = window.top.innerHeight;
        var openViewPortHeight = viewPortHeight - fixedElementsHeight;
        var centerOfViewPort = openViewPortHeight / 2;

        var scrollToX = 0;
        // This can be thought of as a bunch of individual scrolls combined together. (+ is scroll up.  - is scroll down).
        //   1. Start at the top of the page (0).
        //   2. Scroll down from the top of the page the height of the fixed elements.
        //   3. Scroll up from the bottom of the fixed elements to the top of the iFrame.
        //   4. Scroll up from the top of the iFrame to the top of the modal.
        //   5. Scroll up from the top of the modal to the middle of the modal.
        //   6. Scroll down from the center of the modal to the middle of the view port.
        var scrollToY = 0 - fixedElementsHeight + iFrameTopFromWindowTop + topOfModalFromIframeTop + modalViewMiddleFromTopOfModal - centerOfViewPort;

        window.top.scroll(scrollToX, scrollToY);
      }
    },

    /**
     * This method is for intercepting 'focusin' events ('focus' events don't bubble up), and emulating
     * regular tab-order selection, so we can skip past any elements not in the current top-most modal.
     *
     * @method _handleFocusin
     * @param event the triggering 'focusin' event
     * @private
     */
    _handleFocusin: function(event) {
      // let the event happen unabated if we have no top modal or if the element is inside the top modal.
      var targetNotInModal = this._topActiveModal != null && !this._topActiveModal.modalView.el.contains(event.target);
      if (targetNotInModal) {

        var newTarget = null;

        // bail early if 0 or 1 tabbable things in modal
        var doSort = false;
        var modalTabbables = this._getTabbableChildren(this._topActiveModal.modalView.$el, doSort);
        if (modalTabbables.length === 1) {
          newTarget = modalTabbables[0];
        } else if (modalTabbables.length > 1) {

          // get a list of all tabbable elements, sorted in real tabbing order
          doSort = true;
          var allTabbables = this._getTabbableChildren('body', doSort);

          // determine direction by comparing indexes of target and relatedTarget
          var focusIndex = allTabbables.indexOf(event.target);
          var prevIndex = allTabbables.indexOf(event.relatedTarget);
          var direction = 1;
          if (focusIndex < 0) { // how?
            focusIndex = 0;
          }
          if (prevIndex < 0) {
            prevIndex = 0;
          }
          if (prevIndex === allTabbables.length && focusIndex === 0) {
            // catch looping forwards past end
            direction = 1;
          } else if (focusIndex === allTabbables.length && prevIndex === 0) {
            // catch looping backwards past front
            direction = -1;
          } else {
            direction = (prevIndex <= focusIndex) ? 1 : -1;
          }

          // circular loop through sorted array, starting at target, in direction,
          // to find next target that's inside modal
          for (var i = 0; i < allTabbables.length; i++) {
            // use % for circular loop
            var testIndex = (focusIndex + (i * direction) + allTabbables.length) % allTabbables.length;
            newTarget = allTabbables[testIndex];
            if (this._topActiveModal.modalView.el.contains(newTarget)) {
              break;
            }
          }
        }

        // do our best to kill the existing (incoming) focus
        $(event.target).blur();
        if (newTarget) {
          newTarget.focus();
        }
        event.preventDefault();
        return false;
      }
    },

    /**
     * Focus the saved element (from the given modal), if the saved element still exists.
     *
     * @method _resetActiveElement
     * @param modalStackObject {Object} the stack object from which to get the new focused element.
     * @private
     */
    _resetActiveElement: function(modalStackObject) {
      if (modalStackObject.$savedActiveElement
          && $.contains(document.body, modalStackObject.$savedActiveElement)) {
        modalStackObject.$savedActiveElement.focus();
      }
    },

    /**
     * Hides the modal.
     * Intended to be called when a modal gets detached by the lifecycle,
     * so it can be re-shown when re-attached.
     *
     * @method _hideModal
     * @param modalView {View} the view instance to hide.
     * @private
     */
    _hideModal: function(modalView) {
      var modalStackObject = this._getModalObject(modalView);
      if (!modalStackObject) {
        throw new Error('Modal not found!');
      }

      // before hiding, check if we should be restoring the active element
      var restoreActiveElement = false;
      if (this._topActiveModal === modalStackObject) {
        restoreActiveElement = true;
      }

      modalStackObject.$modalEl.remove();
      modalStackObject.hidden = true;
      this._recalculateTopActiveModal();
      // perform re-focus after recalculating top active modal (so it's not disabled by 'focusin' handling)
      if (restoreActiveElement) {
        this._resetActiveElement(modalStackObject);
      }
    },

    /**
     * Re-shows the modal.
     * Intended to be called when a modal gets re-attached by the lifecycle,
     * so it can be re-shown.
     *
     * @method _reshowModal
     * @param modalView {View} the view instance to re-show.
     * @private
     */
    _reshowModal: function(modalView) {
      var modalStackObject = this._getModalObject(modalView);
      if (!modalStackObject) {
        throw new Error('Modal not found!');
      }
      modalStackObject.$placeholderEL.append(modalStackObject.$modalEl);
      modalStackObject.hidden = false;
      this._recalculateTopActiveModal();

      // maybe renew $savedActiveElement
      if (this._topActiveModal === modalStackObject) {
        modalStackObject.$savedActiveElement = $(document.activeElement);
        modalStackObject.$savedActiveElement.blur();
      }
    },

    /**
     * Reattaches all modal children belonging to the given parentView.
     * Intended to be called in the parent's postrender hook, to account for the detachment that occurs
     * at the beginning of the render.
     *
     * @method _reattachModalChildren
     * @param parentView {View} the parent view with modal children to reattach
     * @private
     */
    _reattachModalChildren: function(parentView) {
      _.each(this._modalStack, function(modalStackObject) {
        if (modalStackObject.parentView === parentView) {
          this._reshowModal(modalStackObject.modalView); // render will have called detach, which will have hidden the modal
          var attachmentEl = modalStackObject.$modalEl.find('[inject="modal-content"]');
          modalStackObject.modalView.attachTo(attachmentEl);
        }
      }, this);
    },

    /**
     * Closes the modal and resolves with results.
     *
     * @method _resolveModal
     * @param modalView {View} the view instance to close.
     * @param results {Object} the results with which to call the modal's resolve callback.
     * @private
     */
    _resolveModal: function(modalView, results) {
      var callbacks = this._closeModal(modalView);
      callbacks.resolve(results);
      this._handleBodyScroll();
    },

    /**
     * Closes the modal and rejects with results.
     *
     * @method _rejectModal
     * @param modalView {View} the view instance to cancel.
     * @param results {Object} the results with which to call the modal's reject callback.
     * @private
     */
    _rejectModal: function(modalView, results) {
      var callbacks = this._closeModal(modalView);
      callbacks.reject(results);
      this._handleBodyScroll();
    },

    /**
     * Closes the modal (without detaching) and calls its reject callback.
     * Intended to be called when a modal is being disposed (perhaps by the
     * lifecycle) but hasn't been closed properly.
     *
     * @method _cleanUpModal
     * @param modalView {View} the view instance to clean up.
     * @private
     */
    _cleanUpModal: function(modalView) {
      var callbacks = this._closeModal(modalView);
      callbacks.reject();
    },

    /**
     * Gets the modal object, removes it from the stack, removes the el, stops listening to the modal's events,
     * and returns the pair of callbacks.
     *
     * @method _closeModal
     * @param modalView {View} the modal to remove.
     * @param doDetach {boolean} if true, detach the modal's view as well
     * @return {Object} the pair of callbacks
     * @private
     */
    _closeModal: function(modalView) {
      var modalStackObject = this._getModalObject(modalView);
      if (!modalStackObject) {
        throw new Error('Modal not found!');
      }

      // before closing, check if we should be restoring the active element
      var restoreActiveElement = false;
      if (this._topActiveModal === modalStackObject) {
        restoreActiveElement = true;
      }

      // remove from stack
      var remainingStack = _.reject(this._modalStack, function(modalStackObject) {
        return modalStackObject.modalView === modalView;
      }, this);
      this._modalStack = remainingStack;

      // stop listening before detach, so we don't try to hide in the middle
      this.stopListening(modalStackObject.modalView);
      modalStackObject.parentView.unregisterTrackedView(modalView);
      modalStackObject.modalView.detach();

      // remove from DOM
      modalStackObject.$placeholderEL.remove();

      this._recalculateTopActiveModal();
      // perform re-focus after recalculating top active modal (so it's not disabled by 'focusin' handling)
      if (restoreActiveElement) {
        this._resetActiveElement(modalStackObject);
      }
      return modalStackObject.callbacks;
    }
  }))();
}));
